import type {
  LoopNormals,
  PolygonTopology,
  RenderTriangleIndices,
} from "../types.ts";
import { EDGE_BOUNDARY, EDGE_WIRE } from "../types.ts";

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Validate topology integrity. Throws on failure with a descriptive message.
 * Modeled after Blender's BM_mesh_is_valid — intended to run in dev builds
 * after every topology-modifying operation to catch corruption early.
 */
export function validateTopology(
  topo: PolygonTopology,
  vertexCount: number,
  label?: string,
): void {
  const tag = label ? `[${label}] ` : "";
  const { offsets, corners, edges, cornerEdges, edgeFlags } = topo;

  // 1. Offsets array must have at least 1 element (faceCount + 1)
  if (offsets.length < 1) {
    throw new Error(`${tag}topology offsets array is empty`);
  }

  // 2. First offset must be 0
  if (offsets[0] !== 0) {
    throw new Error(`${tag}offsets[0] is ${offsets[0]}, expected 0`);
  }

  const faceCount = offsets.length - 1;

  // 3. Offsets must be monotonically non-decreasing
  for (let f = 0; f < faceCount; f++) {
    if (offsets[f + 1] < offsets[f]) {
      throw new Error(
        `${tag}offsets not monotonic at face ${f}: offsets[${f}]=${offsets[f]}, offsets[${f + 1}]=${offsets[f + 1]}`,
      );
    }
  }

  // 4. All corner indices must be in bounds
  for (let i = 0; i < corners.length; i++) {
    const vi = corners[i];
    if (vi < 0 || vi >= vertexCount) {
      throw new Error(
        `${tag}corner index ${vi} out of bounds (vertexCount=${vertexCount}) at corners[${i}]`,
      );
    }
  }

  // 5. Every face must have >= 3 corners
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size < 3) {
      throw new Error(`${tag}face ${f} has ${size} corners (minimum 3)`);
    }
  }

  // 6. No face may have duplicate vertex indices
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    const seen = new Set<number>();
    for (let j = 0; j < size; j++) {
      const vi = corners[start + j];
      if (seen.has(vi)) {
        throw new Error(`${tag}face ${f} has duplicate vertex ${vi}`);
      }
      seen.add(vi);
    }
  }

  // 7. No duplicate faces (same vertex set, regardless of winding or start)
  const faceKeys = new Set<string>();
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    const verts: number[] = [];
    for (let j = 0; j < size; j++) verts.push(corners[start + j]);
    verts.sort((a, b) => a - b);
    const key = verts.join(",");
    if (faceKeys.has(key)) {
      throw new Error(
        `${tag}duplicate face detected at face ${f}: vertices [${key}]`,
      );
    }
    faceKeys.add(key);
  }

  // 8. Edge arrays validation
  const edgeCount = edges.length / 2;
  if (edges.length % 2 !== 0) {
    throw new Error(`${tag}edges has odd length ${edges.length}`);
  }
  const seenEdgeKeys = new Set<string>();
  for (let i = 0; i < edges.length; i++) {
    if (edges[i] >= vertexCount) {
      throw new Error(
        `${tag}edge vertex ${edges[i]} out of bounds at edges[${i}] (vertexCount=${vertexCount})`,
      );
    }
  }
  for (let i = 0; i < edgeCount; i++) {
    const a = edges[i * 2];
    const b = edges[i * 2 + 1];
    if (a === b) {
      throw new Error(`${tag}degenerate edge at index ${i}: ${a} === ${b}`);
    }
    if (a > b) {
      throw new Error(
        `${tag}edge ${i} is not in canonical order: [${a}, ${b}]`,
      );
    }
    const key = edgeKey(a, b);
    if (seenEdgeKeys.has(key)) {
      throw new Error(
        `${tag}duplicate edge detected at index ${i}: [${a}, ${b}]`,
      );
    }
    seenEdgeKeys.add(key);
  }
  if (cornerEdges.length !== corners.length) {
    throw new Error(
      `${tag}cornerEdges.length ${cornerEdges.length} !== corners.length ${corners.length}`,
    );
  }
  if (edgeFlags.length !== edgeCount) {
    throw new Error(
      `${tag}edgeFlags.length ${edgeFlags.length} !== edgeCount ${edgeCount}`,
    );
  }

  // 9. Last offset must match corners.length (internal consistency)
  if (offsets[faceCount] !== corners.length) {
    throw new Error(
      `${tag}last offset ${offsets[faceCount]} !== corners.length ${corners.length}`,
    );
  }

  // 10. No zero-area degenerate edges within a face (consecutive identical vertices)
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      const a = corners[start + j];
      const b = corners[start + ((j + 1) % size)];
      if (a === b) {
        throw new Error(
          `${tag}face ${f} has consecutive identical vertices at corner ${j} (vertex ${a})`,
        );
      }
    }
  }

  // 11. cornerEdges must map each face corner to its exact topological edge,
  // and edge flags must match the face/wire usage implied by the topology.
  const faceUsesPerEdge = new Uint32Array(edgeCount);
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      const ci = start + j;
      const ei = cornerEdges[ci];
      if (ei >= edgeCount) {
        throw new Error(
          `${tag}cornerEdges[${ci}] = ${ei} >= edgeCount ${edgeCount}`,
        );
      }
      const a = corners[ci];
      const b = corners[start + ((j + 1) % size)];
      const edgeA = edges[ei * 2];
      const edgeB = edges[ei * 2 + 1];
      if (edgeA !== Math.min(a, b) || edgeB !== Math.max(a, b)) {
        throw new Error(
          `${tag}cornerEdges[${ci}] = ${ei} does not match face edge [${Math.min(a, b)}, ${Math.max(a, b)}]`,
        );
      }
      faceUsesPerEdge[ei]++;
    }
  }

  for (let i = 0; i < edgeCount; i++) {
    const uses = faceUsesPerEdge[i];
    const isWire = (edgeFlags[i] & EDGE_WIRE) !== 0;
    const isBoundary = (edgeFlags[i] & EDGE_BOUNDARY) !== 0;
    if (isWire && uses !== 0) {
      throw new Error(
        `${tag}wire edge ${i} is referenced by ${uses} face corner(s)`,
      );
    }
    if (!isWire && uses === 0) {
      throw new Error(
        `${tag}non-wire edge ${i} is not referenced by any face corner`,
      );
    }
    if (isBoundary !== (!isWire && uses === 1)) {
      throw new Error(
        `${tag}edge ${i} has incorrect boundary flag (uses=${uses}, wire=${isWire})`,
      );
    }
  }
}

/**
 * Validate that a render index buffer is the canonical corner-index fan
 * triangulation implied by the topology.
 */
export function validateCanonicalRenderIndices(
  topo: PolygonTopology,
  indices: RenderTriangleIndices,
  label?: string,
): void {
  const tag = label ? `[${label}] ` : "";
  const { offsets } = topo;
  const faceCount = offsets.length - 1;

  let expectedIndexCount = 0;
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) expectedIndexCount += (size - 2) * 3;
  }

  if (indices.length !== expectedIndexCount) {
    throw new Error(
      `${tag}render index length ${indices.length} !== canonical triangulation length ${expectedIndexCount}`,
    );
  }

  let w = 0;
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 1; j < size - 1; j++) {
      if (
        indices[w] !== start ||
        indices[w + 1] !== start + j ||
        indices[w + 2] !== start + j + 1
      ) {
        throw new Error(
          `${tag}render indices diverge from canonical triangulation at triangle ${w / 3}`,
        );
      }
      w += 3;
    }
  }
}

/**
 * Validate cross-buffer coherence between vertices, topology, normals, and UVs.
 * Ensures all buffer lengths are consistent with each other.
 * Intended for dev builds — throws on any mismatch.
 */
export function validateMeshCoherence(
  vertices: Float32Array,
  topology: PolygonTopology,
  loopNormals: LoopNormals,
  uvs?: Float32Array,
  label?: string,
): void {
  const tag = label ? `[${label}] ` : "";
  const vertexCount = vertices.length / 3;
  const cornerCount = topology.corners.length;

  if (vertices.length % 3 !== 0) {
    throw new Error(
      `${tag}vertices length ${vertices.length} not divisible by 3`,
    );
  }

  if (loopNormals.length !== cornerCount * 3) {
    throw new Error(
      `${tag}loopNormals length ${loopNormals.length} !== cornerCount*3 (${cornerCount * 3}). ` +
        `This usually means per-vertex normals were stored instead of per-loop normals.`,
    );
  }

  if (uvs !== undefined && uvs.length !== cornerCount * 2) {
    throw new Error(
      `${tag}uvs length ${uvs.length} !== cornerCount*2 (${cornerCount * 2})`,
    );
  }

  // Verify all corners reference valid vertex indices
  for (let i = 0; i < cornerCount; i++) {
    if (topology.corners[i] >= vertexCount) {
      throw new Error(
        `${tag}corner[${i}] = ${topology.corners[i]} >= vertexCount ${vertexCount}`,
      );
    }
  }
}
